import { HangoutState } from '../state/HangoutState.js';
import { fetchCharacterSprite } from './character-manager.js';
import { getDisplayManager } from './hangout-display-manager.js';
import { getBackgroundServiceInstance } from './hangout-background-access.js';
import { isVnLayoutMode } from './layout-mode.js';
import { getLatestRoundTurnCharacters } from '../shared/turn-navigation.js';
import { resolveMessageSpriteData, resolveLatestCharacterSpriteData, hasValidResponseActionId } from '../shared/sprite-state.js';
import { hasPlayableCharacterVideo } from '../../../js/utils/spriteVideoStatus.js';
import { parseNumericId } from '../shared/id-utils.js';
import { getTranscriptForLayout } from '../shared/history-resolution.js';
import { markCharacterSpeaking, isMobilePortrait, activateConversationMode } from './mobile-handlers.js';
import {
    getHangoutConfig,
    getCharacterDisplayIndex,
    persistCharacterSpriteMetaInConfig
} from '../shared/hangout-config-utils.js';
import {
    getRoundAssistantMessages,
    normalizeMessagePath,
    resolveHistoricalSceneFromMessages,
    militaryToBackgroundTimeSlot,
    resolveBackgroundTimeSlot
} from '../shared/message-tree.js';

export const resolvePlayableIsVideo = async (characterId, outfitId, actionId) => {
    if (!hasValidResponseActionId(actionId)) return false;
    return hasPlayableCharacterVideo(characterId, outfitId ?? 0, actionId);
};

export const getRunningClockTime = async () => {
    try {
        const { timeProgressionService } = await import('../services/TimeProgressionService.js');
        if (timeProgressionService?.isRunning) return timeProgressionService.getFormattedTime();
    } catch {
        return null;
    }
    return null;
};

export const setClockHour = async (hour) => {
    if (hour < 0 || hour > 23) return;
    const { timeProgressionService } = await import('../services/TimeProgressionService.js');
    if (timeProgressionService?.setHour) await timeProgressionService.setHour(hour);
};

export const clockTimeToMilitary = (clock) => {
    const match = clock ? String(clock).match(/^(\d{1,2}):(\d{2})/) : null;
    return match ? Number(match[1]) * 100 + Number(match[2]) : null;
};

export const resolveSceneFieldsForMessage = async (responseData, hasNewLocation, options = {}) => {
    let locationId = null;
    if (hasNewLocation) {
        locationId = Number.parseInt(String(responseData.location ?? responseData.locationId ?? ''), 10);
    } else {
        const hangoutState = HangoutState.getInstance();
        const messages = options.messages
            || hangoutState.getVisibleMessages?.()
            || hangoutState.getMessages?.()
            || [];
        const targetPath = options.branchPath
            || normalizeMessagePath(responseData?.path)
            || hangoutState.getBranchTipPath?.();
        const scene = resolveHistoricalSceneFromMessages(messages, targetPath);
        locationId = Number.parseInt(String(scene?.locationId ?? ''), 10);
        if (!Number.isFinite(locationId) || locationId < 0) {
            const hangoutConfig = await getHangoutConfig();
            locationId = Number.parseInt(String(hangoutConfig.loc_id ?? hangoutConfig.locId ?? ''), 10);
        }
    }
    if (!Number.isFinite(locationId) || locationId < 0) locationId = null;

    let time = null;
    try {
        time = clockTimeToMilitary(await getRunningClockTime());
    } catch {
        time = null;
    }

    return {
        ...(locationId != null ? { locationId } : {}),
        ...(time != null ? { time } : {})
    };
};

export const refreshVnTurnNavigationFromLatestRound = async (preferredCharacterId = null, options = {}) => {
    if (!isVnLayoutMode()) return;

    const hangoutState = HangoutState.getInstance();
    const hangoutConfig = await getHangoutConfig();
    const activeCharacters = hangoutConfig.allCharacters || [];
    const roundCharacters = getLatestRoundTurnCharacters(
        hangoutState?.getMessages?.() || [],
        activeCharacters,
        options.fallbackCharacters || null
    );

    const displayManager = await getDisplayManager();
    if (!displayManager?.isAvailable?.() || typeof displayManager.setTurnNavigation !== 'function') {
        return;
    }

    displayManager.setTurnNavigation(roundCharacters, {
        preferredCharacterId,
        preserveUserNavigation: options.preserveUserNavigation === true,
        skipSpriteFocus: options.skipSpriteFocus === true
    });
};

export const syncLatestAssistantSpriteFromHistory = async (message, characterIndex = null) => {
    const characterId = message?.characterId ?? message?.character_id;
    if (!characterId) return false;

    let { actionId, outfitId, isVideo } = resolveMessageSpriteData(message, {
        characterId,
        fallbackOutfitId: message?.outfitId ?? message?.outfit_id ?? 0
    });

    if (actionId === undefined || actionId === null) {
        const hangoutState = HangoutState.getInstance();
        const hangoutConfig = await getHangoutConfig();
        const latestSpriteData = resolveLatestCharacterSpriteData({
            messages: hangoutState?.getMessages?.() || [],
            characterId,
            config: hangoutConfig
        });
        if (latestSpriteData.actionId === undefined || latestSpriteData.actionId === null) {
            return false;
        }
        actionId = latestSpriteData.actionId;
        outfitId = latestSpriteData.outfitId ?? outfitId ?? 0;
        isVideo = latestSpriteData.isVideo === true;
    }

    const resolvedCharacterIndex = Number.isInteger(characterIndex)
        ? characterIndex
        : await getCharacterDisplayIndex(characterId);

    await fetchCharacterSprite(
        characterId,
        actionId,
        outfitId,
        resolvedCharacterIndex,
        true,
        isVideo,
        { allowEmotionFallback: false }
    );
    return true;
};

const persistFocusedCharacterSpriteMeta = async (characterId, spriteOptions = null) => {
    const actionNameRaw = typeof spriteOptions?.actionName === 'string' ? spriteOptions.actionName.trim() : '';
    const actionId = spriteOptions?.actionId ?? spriteOptions?.action_id ?? null;
    if (!actionNameRaw && (actionId === null || actionId === undefined || actionId === '')) return;

    const updates = {
        ...(actionId !== null && actionId !== undefined && actionId !== '' ? { actionId } : {}),
        ...(actionNameRaw ? {
            actionName: actionNameRaw,
            spriteState: { action: actionNameRaw }
        } : {})
    };

    const hangoutState = HangoutState.getInstance();
    if (typeof hangoutState?.updateCharacterInConfig === 'function') {
        const current = (hangoutState.getHangoutConfig()?.allCharacters || [])
            .find((character) => String(character?.id) === String(characterId));
        hangoutState.updateCharacterInConfig(characterId, {
            ...updates,
            ...(actionNameRaw ? {
                spriteState: { ...(current?.spriteState || {}), action: actionNameRaw }
            } : {})
        });
        return;
    }

    await persistCharacterSpriteMetaInConfig(characterId, {
        ...updates,
        ...(actionNameRaw ? {
            spriteState: updates.spriteState
        } : {})
    });
};

export const focusHangoutCharacterSprite = async (characterId, spriteOptions = null) => {
    const id = String(characterId ?? '');
    if (!id || id === 'user') return;

    try {
        const characterIndex = await getCharacterDisplayIndex(id);
        const hangoutConfig = await getHangoutConfig();
        const character = (hangoutConfig.allCharacters || []).find((c) => String(c?.id) === id);
        const explicitActionId = spriteOptions?.actionId ?? spriteOptions?.action_id ?? null;
        const hasExplicitAction = explicitActionId !== null && explicitActionId !== undefined && explicitActionId !== '';

        if (hasExplicitAction) {
            const outfitId = spriteOptions?.outfitId ?? spriteOptions?.outfit_id ?? character?.outfitId ?? 0;
            const isVideo = spriteOptions?.isVideo === true || spriteOptions?.is_video === true;
            await fetchCharacterSprite(
                id,
                explicitActionId,
                outfitId,
                characterIndex,
                true,
                isVideo,
                { allowEmotionFallback: false }
            );
            await persistFocusedCharacterSpriteMeta(id, spriteOptions);
        } else {
            const hangoutState = HangoutState.getInstance();
            const messages = hangoutState?.getMessages?.() || [];
            const lastCharacterMessage = messages
                .slice()
                .reverse()
                .find((msg) =>
                    msg?.role === 'assistant'
                    && String(msg.characterId ?? msg.character_id) === id
                );

            if (lastCharacterMessage) {
                await syncLatestAssistantSpriteFromHistory(lastCharacterMessage, characterIndex);
                const resolved = resolveMessageSpriteData(lastCharacterMessage, { characterId: id });
                await persistFocusedCharacterSpriteMeta(id, {
                    actionId: resolved.actionId,
                    actionName: resolved.actionName || lastCharacterMessage.actionName
                });
            } else {
                const spriteData = resolveLatestCharacterSpriteData({
                    messages,
                    characterId: id,
                    config: hangoutConfig
                });
                if (spriteData.actionId != null) {
                    await fetchCharacterSprite(
                        id,
                        spriteData.actionId,
                        spriteData.outfitId ?? character?.outfitId ?? 0,
                        characterIndex,
                        true,
                        spriteData.isVideo === true,
                        { allowEmotionFallback: false }
                    );
                    await persistFocusedCharacterSpriteMeta(id, {
                        actionId: spriteData.actionId,
                        actionName: spriteData.actionName
                    });
                }
            }
        }

        markCharacterSpeaking(characterIndex, id);

        if (isMobilePortrait()) {
            const displayManager = await getDisplayManager();
            await activateConversationMode(
                hangoutConfig.allCharacters || [],
                displayManager
            );
        }

        if (isVnLayoutMode()) {
            const displayManager = await getDisplayManager();
            displayManager.syncMobilePortraitCharacterFocus?.(id);
            const backend = displayManager.displayManager || displayManager;
            backend._syncTurnCursorToCharacterId?.(id);
            backend.displayedCharacterId = id;
            backend._renderDisplayedMessage?.();
        }
    } catch (error) {
        console.warn('focusHangoutCharacterSprite failed:', characterId, error);
    }
};

export const applyHistoricalHangoutScene = async (messages = [], targetPath, options = null) => {
    const hangoutState = HangoutState.getInstance();
    const scene = options?.scene || resolveHistoricalSceneFromMessages(messages, targetPath);
    const allowCurrentLocationFallback = options?.allowCurrentLocationFallback === true;

    let locationId = scene?.locationId ?? null;
    if (locationId == null && allowCurrentLocationFallback) {
        const hangoutConfig = await getHangoutConfig();
        const fallback = Number.parseInt(String(hangoutConfig.loc_id ?? hangoutConfig.locId ?? ''), 10);
        if (Number.isFinite(fallback) && fallback >= 0) locationId = fallback;
    }

    const timeSlot = resolveBackgroundTimeSlot(scene?.timeOfDay)
        || militaryToBackgroundTimeSlot(scene?.time)
        || resolveBackgroundTimeSlot(hangoutState.getCurrentTimeSlot())
        || 'night';

    if (locationId == null) {
        return { success: false, locationId: null, timeSlot, time: scene?.time ?? null };
    }

    try {
        const backgroundService = await getBackgroundServiceInstance();
        await backgroundService.setBackground(locationId, timeSlot);
    } catch (backgroundError) {
        console.warn('applyHistoricalHangoutScene: background restore failed', backgroundError);
        return { success: false, locationId, timeSlot, time: scene?.time ?? null };
    }

    try {
        await hangoutState.switchLocation({
            id: locationId,
            timeOfDay: timeSlot
        });
    } catch (configError) {
        console.warn('applyHistoricalHangoutScene: loc_id update failed', configError);
    }

    try {
        await hangoutState.setTimeSlot(timeSlot, false);
    } catch (timeSlotError) {
        console.warn('applyHistoricalHangoutScene: setTimeSlot failed', timeSlotError);
    }

    if (scene?.time != null && Number.isFinite(Number(scene.time))) {
        try {
            await setClockHour(Math.floor(Math.trunc(Number(scene.time)) / 100));
        } catch (clockError) {
            console.warn('applyHistoricalHangoutScene: clock restore failed', clockError);
        }
    }

    return { success: true, locationId, timeSlot, time: scene?.time ?? null };
};

export const focusHangoutConversationTurn = async ({
    path = null,
    characterId,
    spriteOptions = null
} = {}) => {
    const clickedId = String(characterId ?? '');
    if (!clickedId || clickedId === 'user') return;

    const hangoutState = HangoutState.getInstance();
    const allMessages = hangoutState.getMessages() || [];
    const visible = getTranscriptForLayout(hangoutState);
    const sceneMessages = allMessages.length > 0 ? allMessages : visible;
    const normalizedPath = normalizeMessagePath(path);

    const parsedLocationId = parseNumericId(spriteOptions?.locationId ?? spriteOptions?.location_id);
    const clickedLocationId = parsedLocationId != null && parsedLocationId >= 0 ? parsedLocationId : null;
    const historicalScene = normalizedPath
        ? resolveHistoricalSceneFromMessages(sceneMessages, normalizedPath)
        : { locationId: null, time: null, currentTime: null, timeOfDay: null };
    const scene = {
        ...historicalScene,
        ...(clickedLocationId != null ? { locationId: clickedLocationId } : {})
    };

    if (normalizedPath || scene.locationId != null) {
        await applyHistoricalHangoutScene(sceneMessages, normalizedPath, {
            scene,
            allowCurrentLocationFallback: false
        });
    }

    const hangoutConfig = await getHangoutConfig();
    const configById = new Map(
        (hangoutConfig.allCharacters || []).map((character) => [String(character?.id), character])
    );
    const roundAssistants = normalizedPath
        ? getRoundAssistantMessages(visible, normalizedPath)
        : [];

    const resolveSpriteThroughHistory = (message, id) => {
        const fallbackOutfitId = configById.get(id)?.outfitId ?? 0;
        const direct = resolveMessageSpriteData(message, { characterId: id, fallbackOutfitId });
        if (direct.actionId != null) {
            return {
                actionId: direct.actionId,
                outfitId: direct.outfitId,
                isVideo: direct.isVideo === true,
                actionName: direct.actionName || message?.actionName || null
            };
        }

        const messagePath = normalizeMessagePath(message?.path);
        const messageIndex = visible.findIndex((entry) =>
            normalizeMessagePath(entry?.path) === messagePath
            && String(entry?.characterId ?? entry?.character_id ?? '') === id
        );
        const searchEnd = messageIndex >= 0 ? messageIndex : visible.length - 1;
        for (let index = searchEnd; index >= 0; index -= 1) {
            const candidate = visible[index];
            if (String(candidate?.characterId ?? candidate?.character_id ?? '') !== id) continue;
            if (candidate?.role !== 'assistant') continue;
            const spriteData = resolveMessageSpriteData(candidate, { characterId: id, fallbackOutfitId });
            if (spriteData.actionId != null) {
                return {
                    actionId: spriteData.actionId,
                    outfitId: spriteData.outfitId,
                    isVideo: spriteData.isVideo === true,
                    actionName: spriteData.actionName || candidate?.actionName || null
                };
            }
        }
        return {
            actionId: direct.actionId,
            outfitId: direct.outfitId ?? fallbackOutfitId,
            isVideo: direct.isVideo === true,
            actionName: direct.actionName || message?.actionName || null
        };
    };

    const spritesByCharacter = new Map();
    for (const message of roundAssistants) {
        const id = String(message?.characterId ?? message?.character_id ?? '');
        if (!id || id === 'user') continue;
        spritesByCharacter.set(id, resolveSpriteThroughHistory(message, id));
    }

    if (spriteOptions && (spriteOptions.actionId != null || spriteOptions.action_id != null)) {
        spritesByCharacter.set(clickedId, {
            actionId: spriteOptions.actionId ?? spriteOptions.action_id,
            outfitId: spriteOptions.outfitId ?? spriteOptions.outfit_id ?? configById.get(clickedId)?.outfitId,
            isVideo: spriteOptions.isVideo === true || spriteOptions.is_video === true,
            actionName: spriteOptions.actionName || null
        });
    } else if (!spritesByCharacter.has(clickedId) && spriteOptions) {
        spritesByCharacter.set(clickedId, spriteOptions);
    }

    for (const [id, options] of spritesByCharacter) {
        if (id === clickedId) continue;
        await focusHangoutCharacterSprite(id, options);
    }
    await focusHangoutCharacterSprite(clickedId, spritesByCharacter.get(clickedId) || spriteOptions);
};

export { getCharacterDisplayIndex };
